import React, { useMemo } from "react";
import Field from "./shared/Field";

function normalizeFeaturesTemplate(tpl) {
  const t = String(tpl ?? "").toLowerCase().trim();

  if (t === "1" || t === "template1" || t.endsWith("template1")) return "template1";
  if (t === "2" || t === "template2" || t.endsWith("template2")) return "template2";
  if (t === "3" || t === "template3" || t.endsWith("template3")) return "template3";

  return "template1";
}

function Group({ title, subtitle, defaultOpen = true, children, toggleLabel }) {
  return (
    <details open={defaultOpen} className="rounded-xl border border-gray-200 bg-white">
      <summary className="cursor-pointer select-none list-none px-4 py-3 flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">{title}</div>
          {subtitle ? <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div> : null}
        </div>
        <div className="text-xs text-gray-400 mt-0.5">{toggleLabel || "Toggle"}</div>
      </summary>
      <div className="px-4 pb-4 pt-1">{children}</div>
    </details>
  );
}

function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex p-1 rounded-xl bg-gray-100 border border-gray-200">
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              active
                ? "bg-white text-gray-900 shadow-sm ring-1 ring-gray-200"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

const makeNewFeature = (tplKey) => {
  const base = {
    icon: "",
    title: "",
    description: "",
    benefits: [],
  };

  if (tplKey === "template3") {
    return {
      ...base,
      details: "",
      accentFrom: "#3b82f6",
      accentTo: "#a855f7",
    };
  }

  return base;
};

const makeDefaultPanel = () => ({
  bgFrom: "#111827",
  bgTo: "#000000",
  textColor: "#ffffff",
  mutedTextColor: "#d1d5db",

  statCardBg: "#ffffff",
  statCardOpacity: 0.1,

  stats: [
    { value: "99.9%", label: "Uptime" },
    { value: "256-bit", label: "Encryption" },
    { value: "24/7", label: "Support" },
    { value: "100+", label: "Integrations" },
  ],

  previewEmoji: "📈",
  previewText: "Live Dashboard Preview",
  previewBgFrom: "#1f2937",
  previewBgTo: "#111827",
});

const makeDefaultFloatingBadge = () => ({
  title: "AI-Powered",
  subtitle: "Insights Engine",
  titleColor: "#2563eb",
  subtitleColor: "#4b5563",
  bg: "#ffffff",
});

export default function FeaturesSectionEditor({
  config,
  template,
  onChange,
  t = (k, f) => f,
}) {
  const cfg = config || {};
  const tplKey = useMemo(() => normalizeFeaturesTemplate(template), [template]);

  const features = Array.isArray(cfg.features) ? cfg.features : [];
  const panel = cfg.panel || (tplKey === "template3" ? makeDefaultPanel() : undefined);
  const floatingBadge =
    cfg.floatingBadge || (tplKey === "template3" ? makeDefaultFloatingBadge() : undefined);

  const updateFeature = (index, key, value) => {
    const updated = [...features];
    updated[index] = { ...(updated[index] || {}), [key]: value };
    onChange?.("features", updated);
  };

  const addFeature = () => {
    onChange?.("features", [...features, makeNewFeature(tplKey)]);
  };

  const removeFeature = (index) => {
    const updated = [...features];
    updated.splice(index, 1);
    onChange?.("features", updated);
  };

  const updateBenefits = (index, value) => {
    const updated = [...features];
    updated[index] = {
      ...(updated[index] || {}),
      benefits: value.split("\n").filter(Boolean),
    };
    onChange?.("features", updated);
  };

  const setPanelField = (key, value) => {
    const base = panel || makeDefaultPanel();
    onChange?.("panel", { ...base, [key]: value });
  };

  const setBadgeField = (key, value) => {
    const base = floatingBadge || makeDefaultFloatingBadge();
    onChange?.("floatingBadge", { ...base, [key]: value });
  };

  const stats = Array.isArray(panel?.stats) ? panel.stats : [];

  const addStat = () => {
    const next = [...stats, { value: "", label: "" }];
    setPanelField("stats", next);
  };

  const removeStat = (index) => {
    const next = [...stats];
    next.splice(index, 1);
    setPanelField("stats", next);
  };

  const updateStat = (index, key, value) => {
    const next = [...stats];
    next[index] = { ...(next[index] || {}), [key]: value };
    setPanelField("stats", next);
  };

  return (
    <div className="space-y-4">
      <Group
        title={t("featuresEditor.group.basics", "Section Basics")}
        subtitle={t("featuresEditor.group.basicsHelp", "Main content and layout direction")}
        toggleLabel={t("featuresEditor.group.basicsToggle", "Toggle")}
        defaultOpen
      >
        <div className="space-y-4">
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">
              {t("featuresEditor.header.layoutDirection", "Layout Direction")}
            </div>

            <Segmented
              value={cfg.direction || "ltr"}
              onChange={(v) => onChange?.("direction", v)}
              options={[
                { value: "ltr", label: t("featuresEditor.direction.ltr", "LTR") },
                { value: "rtl", label: t("featuresEditor.direction.rtl", "RTL") },
              ]}
            />

            <div className="text-xs text-gray-500 mt-2">
              {t(
                "featuresEditor.header.layoutDirectionHelp",
                "Use RTL for Arabic and Hebrew, LTR for English."
              )}
            </div>
          </div>

          <Field
            label={t("featuresEditor.header.title", "Title")}
            value={cfg.title || ""}
            onChange={(v) => onChange?.("title", v)}
          />

          <Field
            label={t("featuresEditor.header.subtitle", "Subtitle")}
            type="textarea"
            value={cfg.subtitle || ""}
            onChange={(v) => onChange?.("subtitle", v)}
          />

          {tplKey === "template2" && (
            <>
              <Field
                label={t("featuresEditor.header.badgeText", "Badge Text")}
                value={cfg.badgeText || ""}
                onChange={(v) => onChange?.("badgeText", v)}
              />

              <Field
                label={t("featuresEditor.header.buttonText", "Button Text")}
                value={cfg.buttonText || ""}
                onChange={(v) => onChange?.("buttonText", v)}
              />
            </>
          )}
        </div>
      </Group>

      <Group
        title={t("featuresEditor.group.features", "Feature Cards")}
        subtitle={t(
          "featuresEditor.group.featuresHelp",
          "Manage the feature items shown in the section"
        )}
        toggleLabel={t("featuresEditor.group.featuresToggle", "Toggle")}
        defaultOpen
      >
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
              {t("featuresEditor.features.title", "Features")}
            </h3>

            <button
              type="button"
              onClick={addFeature}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-white"
            >
              {t("featuresEditor.features.addFeature", "Add Feature")}
            </button>
          </div>

          {features.map((feature, index) => (
            <div
              key={index}
              className="p-4 border border-gray-200 rounded-xl space-y-4 bg-gray-50"
            >
              <div className="flex items-center justify-between">
                <div className="font-medium text-gray-700">
                  {t("featuresEditor.features.feature", "Feature")} {index + 1}
                </div>

                <button
                  type="button"
                  onClick={() => removeFeature(index)}
                  className="text-red-500 text-sm hover:underline"
                >
                  {t("featuresEditor.common.remove", "Remove")}
                </button>
              </div>

              <Field
                label={t("featuresEditor.features.icon", "Icon (emoji)")}
                value={feature.icon || ""}
                onChange={(v) => updateFeature(index, "icon", v)}
                placeholder={t("featuresEditor.features.iconPlaceholder", "📊 or 🚀 or 🔒")}
              />

              <Field
                label={t("featuresEditor.features.titleField", "Title")}
                value={feature.title || ""}
                onChange={(v) => updateFeature(index, "title", v)}
              />

              <Field
                label={t("featuresEditor.features.description", "Description")}
                type="textarea"
                value={feature.description || ""}
                onChange={(v) => updateFeature(index, "description", v)}
              />

              <Field
                label={t("featuresEditor.features.benefits", "Benefits (one per line)")}
                type="textarea"
                value={(feature.benefits || []).join("\n")}
                onChange={(v) => updateBenefits(index, v)}
              />

              {tplKey === "template3" && (
                <div className="space-y-4 pt-2">
                  <Field
                    label={t(
                      "featuresEditor.template3.details",
                      "Details (shown on the right panel)"
                    )}
                    type="textarea"
                    value={feature.details || ""}
                    onChange={(v) => updateFeature(index, "details", v)}
                    placeholder={t(
                      "featuresEditor.template3.detailsPlaceholder",
                      "Longer explanation shown in the dark panel"
                    )}
                  />

                  <div className="grid md:grid-cols-2 gap-4">
                    <Field
                      label={t("featuresEditor.template3.accentFrom", "Accent From")}
                      type="color"
                      value={feature.accentFrom || "#3b82f6"}
                      onChange={(v) => updateFeature(index, "accentFrom", v)}
                    />
                    <Field
                      label={t("featuresEditor.template3.accentTo", "Accent To")}
                      type="color"
                      value={feature.accentTo || "#a855f7"}
                      onChange={(v) => updateFeature(index, "accentTo", v)}
                    />
                  </div>

                  <div className="text-xs text-gray-500">
                    {t(
                      "featuresEditor.template3.gradientNote",
                      "These colors control the feature icon gradient in template 3."
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

          {features.length === 0 ? (
            <div className="text-sm text-gray-500">
              {t(
                "featuresEditor.features.empty",
                "No features yet. Click Add Feature to create the first one."
              )}
            </div>
          ) : null}
        </div>
      </Group>

      {tplKey === "template3" && (
        <Group
          title={t("featuresEditor.group.advancedPanel", "Template 3 Advanced Panel")}
          subtitle={t(
            "featuresEditor.group.advancedPanelHelp",
            "Right panel styling, stats, preview area, and floating badge"
          )}
          toggleLabel={t("featuresEditor.group.advancedPanelToggle", "Toggle")}
          defaultOpen={false}
        >
          <div className="space-y-6">
            <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 space-y-5">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                  {t("featuresEditor.template3.rightPanelTitle", "Right Panel (Template 3)")}
                </h3>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <Field
                  label={t("featuresEditor.template3.panelBgFrom", "Panel Background From")}
                  type="color"
                  value={panel?.bgFrom || "#111827"}
                  onChange={(v) => setPanelField("bgFrom", v)}
                />
                <Field
                  label={t("featuresEditor.template3.panelBgTo", "Panel Background To")}
                  type="color"
                  value={panel?.bgTo || "#000000"}
                  onChange={(v) => setPanelField("bgTo", v)}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <Field
                  label={t("featuresEditor.template3.panelTextColor", "Panel Text Color")}
                  type="color"
                  value={panel?.textColor || "#ffffff"}
                  onChange={(v) => setPanelField("textColor", v)}
                />
                <Field
                  label={t(
                    "featuresEditor.template3.panelMutedTextColor",
                    "Panel Muted Text Color"
                  )}
                  type="color"
                  value={panel?.mutedTextColor || "#d1d5db"}
                  onChange={(v) => setPanelField("mutedTextColor", v)}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <Field
                  label={t("featuresEditor.template3.statCardBg", "Stat Card Background")}
                  type="color"
                  value={panel?.statCardBg || "#ffffff"}
                  onChange={(v) => setPanelField("statCardBg", v)}
                />
                <Field
                  label={t(
                    "featuresEditor.template3.statCardOpacity",
                    "Stat Card Opacity (0 to 1)"
                  )}
                  type="number"
                  value={panel?.statCardOpacity ?? 0.1}
                  onChange={(v) => setPanelField("statCardOpacity", Number(v))}
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-gray-700">
                    {t("featuresEditor.template3.stats", "Stats")}
                  </div>
                  <button
                    type="button"
                    onClick={addStat}
                    className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-white"
                  >
                    {t("featuresEditor.template3.addStat", "Add Stat")}
                  </button>
                </div>

                {stats.length === 0 ? (
                  <div className="text-sm text-gray-600">
                    {t(
                      "featuresEditor.template3.noStats",
                      "No stats yet. Add at least 4 for best layout."
                    )}
                  </div>
                ) : null}

                <div className="space-y-3">
                  {stats.map((s, i) => (
                    <div key={i} className="p-3 bg-white rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-sm font-medium text-gray-700">
                          {t("featuresEditor.template3.stat", "Stat")} {i + 1}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeStat(i)}
                          className="text-red-500 text-sm hover:underline"
                        >
                          {t("featuresEditor.common.remove", "Remove")}
                        </button>
                      </div>

                      <div className="grid md:grid-cols-2 gap-3">
                        <Field
                          label={t("featuresEditor.template3.value", "Value")}
                          value={s?.value || ""}
                          onChange={(v) => updateStat(i, "value", v)}
                          placeholder={t(
                            "featuresEditor.template3.valuePlaceholder",
                            "99.9%"
                          )}
                        />
                        <Field
                          label={t("featuresEditor.template3.label", "Label")}
                          value={s?.label || ""}
                          onChange={(v) => updateStat(i, "label", v)}
                          placeholder={t(
                            "featuresEditor.template3.labelPlaceholder",
                            "Uptime"
                          )}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <Field
                  label={t("featuresEditor.template3.previewEmoji", "Preview Emoji")}
                  value={panel?.previewEmoji || "📈"}
                  onChange={(v) => setPanelField("previewEmoji", v)}
                />
                <Field
                  label={t("featuresEditor.template3.previewText", "Preview Text")}
                  value={panel?.previewText || "Live Dashboard Preview"}
                  onChange={(v) => setPanelField("previewText", v)}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <Field
                  label={t(
                    "featuresEditor.template3.previewBgFrom",
                    "Preview Background From"
                  )}
                  type="color"
                  value={panel?.previewBgFrom || "#1f2937"}
                  onChange={(v) => setPanelField("previewBgFrom", v)}
                />
                <Field
                  label={t("featuresEditor.template3.previewBgTo", "Preview Background To")}
                  type="color"
                  value={panel?.previewBgTo || "#111827"}
                  onChange={(v) => setPanelField("previewBgTo", v)}
                />
              </div>
            </div>

            <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 space-y-4">
              <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                {t("featuresEditor.template3.floatingBadge", "Floating Badge")}
              </h4>

              <div className="grid md:grid-cols-2 gap-4">
                <Field
                  label={t("featuresEditor.template3.badgeTitle", "Title")}
                  value={floatingBadge?.title || "AI-Powered"}
                  onChange={(v) => setBadgeField("title", v)}
                />
                <Field
                  label={t("featuresEditor.template3.badgeSubtitle", "Subtitle")}
                  value={floatingBadge?.subtitle || "Insights Engine"}
                  onChange={(v) => setBadgeField("subtitle", v)}
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <Field
                  label={t("featuresEditor.template3.badgeBackground", "Badge Background")}
                  type="color"
                  value={floatingBadge?.bg || "#ffffff"}
                  onChange={(v) => setBadgeField("bg", v)}
                />
                <Field
                  label={t("featuresEditor.template3.badgeTitleColor", "Title Color")}
                  type="color"
                  value={floatingBadge?.titleColor || "#2563eb"}
                  onChange={(v) => setBadgeField("titleColor", v)}
                />
                <Field
                  label={t("featuresEditor.template3.badgeSubtitleColor", "Subtitle Color")}
                  type="color"
                  value={floatingBadge?.subtitleColor || "#4b5563"}
                  onChange={(v) => setBadgeField("subtitleColor", v)}
                />
              </div>
            </div>
          </div>
        </Group>
      )}

      {tplKey === "template2" && (
        <Group
          title={t("featuresEditor.group.bottomCta", "Bottom Call To Action")}
          subtitle={t(
            "featuresEditor.group.bottomCtaHelp",
            "Text shown in the bottom CTA area"
          )}
          toggleLabel={t("featuresEditor.group.bottomCtaToggle", "Toggle")}
          defaultOpen={false}
        >
          <div className="space-y-4">
            <Field
              label={t("featuresEditor.bottomCta.bottomTitle", "Bottom Title")}
              value={cfg.bottomCta?.title || ""}
              onChange={(v) =>
                onChange?.("bottomCta", {
                  ...(cfg.bottomCta || {}),
                  title: v,
                })
              }
            />

            <Field
              label={t("featuresEditor.bottomCta.bottomSubtitle", "Bottom Subtitle")}
              type="textarea"
              value={cfg.bottomCta?.subtitle || ""}
              onChange={(v) =>
                onChange?.("bottomCta", {
                  ...(cfg.bottomCta || {}),
                  subtitle: v,
                })
              }
            />
          </div>
        </Group>
      )}
    </div>
  );
}